import { SolButton, SolIcon } from "@solstice/sol-react";
import { EditorState, RichUtils } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import { convertFromHTML, convertToHTML } from "draft-convert";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { BrokerProps, ProfileProps } from "types";
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import {
  bioValidation,
  getApiPayloadFromForm,
  getFormatedDateTime,
  parseOriginalJSON,
} from "utils";
import { useFormik } from "formik";
import cn from "classnames";
import { useApiCall } from "hooks";
import {
  getProfilePublishListAPI,
  patchProfileAPI,
  postProfilePublishAPI,
} from "services";
import { GlobalContext } from "context";
import {
  PUBLISH_STATUS_PUBLISHED,
  SET_ALERT_MODAL,
  SET_LOADING_MODAL,
} from "constant";
// @ts-ignore
import { getSelectedBlocksType } from "draftjs-utils";
import { Tooltip } from "react-tooltip";

interface ManualBioProps {
  profile: ProfileProps | null;
  setProfile: any;
  broker?: BrokerProps | null;
  reloadBroker: () => void;
}

const INITIAL_VALUES = {
  bio: EditorState.createEmpty(),
};

const validationSchema = Yup.object().shape({
  bio: Yup.string().required("This field is required"),
});

const ManualBio: React.FC<ManualBioProps> = ({
  profile,
  setProfile,
  broker,
  reloadBroker,
}) => {
  const { dispatch } = useContext(GlobalContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [patchProfile] = useApiCall(patchProfileAPI);
  const stripEmptyH2Tags = useCallback((html: string): string => {
    const regex = /<h2><\/h2>/g;
    return html.replace(regex, "");
  }, []);

  useEffect(() => {
    if (!profile || profile?.id?.toString() !== searchParams?.get("tab"))
      return;
    let formValues: any = {};
    Object.keys(INITIAL_VALUES)?.forEach((key: string) => {
      if (key === "bio") {
        if (!profile?.bio) {
          formValues = {
            ...formValues,
            [key]: EditorState.createEmpty(),
          };
          return;
        }

        const rawContentState = parseOriginalJSON(profile?.bio);
        const contentState: any = convertFromHTML(
          stripEmptyH2Tags(rawContentState)
        );
        formValues = {
          ...formValues,
          [key]: EditorState.createWithContent(contentState),
        };
        return;
      }
      formValues = {
        ...formValues,
        [key]: (profile as any)?.[key] || "",
      };
    });
    setValues({
      ...formValues,
    });
  }, [profile]);

  const { handleBlur, setFieldValue, values, errors, touched, setValues } =
    useFormik({
      initialValues: INITIAL_VALUES,
      validationSchema: validationSchema,
      onSubmit: async () => {},
    });

  const handleUpdate = (key: string, value: any) => {
    try {
      if (key === "bio") {
        const contentState = values?.bio?.getCurrentContent();
        const rawContentState = convertToHTML(contentState);
        const contentString = stripEmptyH2Tags(rawContentState);
        value = contentString;
        let payload = getApiPayloadFromForm(profile, {
          bio: value,
        });
        if (!payload || !Object.keys(payload)?.length) {
          searchParams.set("mode", "general");
          setSearchParams(searchParams);
          return;
        }

        const action = () => {
          try {
            dispatch({
              type: SET_LOADING_MODAL,
              payload: {
                open: true,
                title: "Saving bio content",
              },
            });
            patchProfile({
              pk: profile?.id,
              [key]: value,
            })
              .then((res: any) => {
                if (!res) return;
                setProfile(res);
                reloadBroker();
              })
              .finally(() => {
                dispatch({
                  type: SET_LOADING_MODAL,
                  payload: {
                    open: false,
                    title: "",
                  },
                });
                searchParams.set("mode", "general");
                setSearchParams(searchParams);
              });
          } catch (err) {
            dispatch({
              type: SET_LOADING_MODAL,
              payload: {
                open: false,
                title: "",
              },
            });
          }
        };
        const validate = bioValidation(value, broker?.leadership !== 1);
        if (!!validate?.length) {
          dispatch({
            type: SET_ALERT_MODAL,
            payload: {
              open: true,
              kind: "error",
              title: `Bio format warning`,
              description: validate,
              icon: "warning",
              btn3: null,
              btn1: {
                label: "Cancel",
                onClick: () => {
                  dispatch({
                    type: SET_ALERT_MODAL,
                    payload: {
                      open: false,
                      kind: "",
                      title: "",
                      description: "",
                      icon: "",
                      btn1: null,
                      btn2: null,
                      btn3: null,
                    },
                  });
                },
              },
              btn2: {
                label: "Save Anyway",
                onClick: () => {
                  dispatch({
                    type: SET_ALERT_MODAL,
                    payload: {
                      open: false,
                      kind: "",
                      title: "",
                      description: "",
                      icon: "",
                      btn1: null,
                      btn2: null,
                      btn3: null,
                    },
                  });
                  action();
                },
              },
            },
          });
          return;
        }
        action();
      }
    } catch (err) {}
  };

  const handleChange = (editorState: EditorState) => {
    setFieldValue("bio", editorState);
  };

  const saveDraftTooltip = useMemo((): string => {
    if (!broker?.update_timestamp) return "Bio has not been saved";
    const updatedDatetime = getFormatedDateTime(broker.update_timestamp);
    let author: string | null = null;
    if (broker?.updated_user_first_name) {
      author = broker.updated_user_first_name;
      if (broker?.updated_user_last_name) {
        author += ` ${broker.updated_user_last_name}`;
      }
    } else if (broker?.updated_user_email) {
      author = broker.updated_user_email;
    }
    const tooltipMessage = author
      ? `Last Saved by ${author} on ${updatedDatetime}`
      : `Last Saved on ${updatedDatetime}`;
    return tooltipMessage;
  }, [broker]);

  const handleKeyCommand = useCallback(
    (command: string, editorState: EditorState) => {
      const newState = RichUtils.handleKeyCommand(editorState, command);
      if (newState) {
        if (["bold", "italic", "underline", "code"].includes(command)) {
          return "not-handled";
        }
        return "handled";
      }
      return "not-handled";
    },
    []
  );

  return (
    <>
      <div className="flex flex-col">
        <div className="flex flex-row items-center justify-between mb-6">
          <h2 className="text-xl mb-1">Bio - Manual update</h2>
          <div className="flex flex-row items-center space-x-4">
            <SolButton
              variant="tertiary"
              onSol-click={() => {
                searchParams.set("mode", "general");
                setSearchParams(searchParams);
              }}
            >
              Cancel
            </SolButton>
            <SolButton
              variant="secondary"
              onSol-click={() => {
                handleUpdate("bio", values?.bio);
              }}
              data-tooltip-id="bio_save_draft_tooltip"
              data-tooltip-content={saveDraftTooltip}
            >
              Save draft
            </SolButton>
            <Tooltip id="bio_save_draft_tooltip" className="z-50" />
          </div>
        </div>

        <div className="absolute top-[92px] left-0 right-0 bottom-0 px-10 overflow-y-auto">
          <div className="border border-jll-light-stroke-default rounded mb-6">
            <Editor
              editorState={values?.bio as any}
              wrapperClassName="border-none"
              editorClassName={cn("px-5", {})}
              onEditorStateChange={handleChange}
              onBlur={(e) => {
                handleBlur(e);
              }}
              toolbar={{ options: [] }}
              toolbarHidden={true}
              handleKeyCommand={handleKeyCommand}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ManualBio;
